import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clearNotifications,
  dismissToast,
  markAllNotificationsRead,
  markNotificationRead,
  useStore,
  type NotificationItem,
} from '../lib/store.js';
import { IconCheck, IconClose, IconWarning } from './Icons.js';

/**
 * Bell button + dropdown panel, plus a toast layer for fresh events. The
 * notification list is populated by the store as transitions happen
 * (machines flipping offline, servers crashing, bulk runs finishing).
 */
export function NotificationBell() {
  const items = useStore((s) => s.notifications);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const unread = items.filter((n) => !n.read).length;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Mark all read when the panel opens — same affordance as Slack/Linear.
  useEffect(() => {
    if (open && unread > 0) {
      const t = setTimeout(() => markAllNotificationsRead(), 600);
      return () => clearTimeout(t);
    }
  }, [open, unread]);

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-icon relative"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        aria-expanded={open}
        title="Notifications"
      >
        <BellIcon />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-status-error text-[10px] font-bold text-white flex items-center justify-center ring-2 ring-bg-raised tabular-nums"
            aria-hidden
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-1rem)] card shadow-modal overflow-hidden animate-slide-up z-50"
          role="dialog"
          aria-label="Notifications"
        >
          <header className="px-4 h-11 border-b border-bg-border flex items-center justify-between bg-bg-raised/40">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-fg">Notifications</h3>
              {items.length > 0 && (
                <span className="text-[11px] text-fg-dim tabular-nums">{items.length}</span>
              )}
            </div>
            {items.length > 0 && (
              <button className="btn-quiet btn-sm !h-7 !px-2 !text-[11px]" onClick={() => { clearNotifications(); setOpen(false); }}>
                Clear
              </button>
            )}
          </header>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
                  <BellIcon />
                </div>
                <div className="text-sm text-fg-muted">You're all caught up</div>
                <div className="text-xs text-fg-dim max-w-[220px]">
                  Crashes, agent disconnects, and bulk results show up here.
                </div>
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li key={n.id}>
                    <NotifRow item={n} onClose={() => setOpen(false)} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ item, onClose }: { item: NotificationItem; onClose: () => void }) {
  const tone = TONE[item.level];
  const body = (
    <div className="px-4 py-3 flex gap-3 hover:bg-bg-hover/40 transition-colors cursor-pointer">
      <span className={`mt-0.5 shrink-0 ${tone.fg}`}>{tone.icon}</span>
      <div className="flex-1 min-w-0">
        <div className={`text-sm leading-snug ${item.read ? 'text-fg-muted' : 'text-fg font-medium'}`}>
          {item.title}
        </div>
        {item.message && (
          <div className="text-xs text-fg-dim mt-0.5 truncate">{item.message}</div>
        )}
        <div className="text-[10px] text-fg-dim mt-1 tabular-nums">{relTime(item.ts)}</div>
      </div>
      {!item.read && <span className="dot bg-accent shrink-0 mt-1.5" aria-label="Unread" />}
    </div>
  );

  if (item.link) {
    return (
      <Link
        to={item.link}
        onClick={() => { markNotificationRead(item.id); onClose(); }}
        className="block"
      >
        {body}
      </Link>
    );
  }
  return <div onClick={() => markNotificationRead(item.id)}>{body}</div>;
}

/** Stack of recent toasts in the corner of the viewport. */
export function ToastStack() {
  const toasts = useStore((s) => s.toasts);
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[55] flex flex-col gap-2 items-end pointer-events-none">
      {toasts.map((t) => {
        const tone = TONE[t.level];
        return (
          <div
            key={t.id}
            className={`pointer-events-auto card pop !shadow-pop animate-slide-up flex gap-3 px-4 py-3 max-w-sm ring-1 ${tone.ring}`}
            role="status"
          >
            <span className={`shrink-0 mt-0.5 ${tone.fg}`}>{tone.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-fg leading-snug">{t.title}</div>
              {t.message && <div className="text-xs text-fg-muted mt-0.5">{t.message}</div>}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              className="btn-icon !w-6 !h-6 -mr-1 -mt-1"
              aria-label="Dismiss"
            >
              <IconClose className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

const TONE: Record<NotificationItem['level'], { fg: string; ring: string; icon: React.ReactNode }> = {
  info:    { fg: 'text-status-updating', ring: 'ring-status-updating/30', icon: <BellIcon /> },
  success: { fg: 'text-status-online',   ring: 'ring-status-online/30',   icon: <IconCheck /> },
  warn:    { fg: 'text-status-warn',     ring: 'ring-status-warn/30',     icon: <IconWarning /> },
  error:   { fg: 'text-status-error',    ring: 'ring-status-error/30',    icon: <IconWarning /> },
};

function BellIcon() {
  return (
    <svg
      width={16} height={16} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden className="shrink-0"
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function relTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
